/**
 * SyncManager.js
 *
 * Decoupled background sync engine. Processes the SQLite PendingOrders queue,
 * POSTing orders to the API in chronological order with retry / back-off logic.
 *
 * Triggers:
 *   1. Network comes back online (NetInfo listener)
 *   2. A new order is inserted into the queue (manual kick)
 *
 * Retry policy:
 *   - Attempts up to MAX_RETRIES (5) per order.
 *   - On exceeding threshold the order is marked 'Failed' and a notification fires.
 *
 * Idempotency:
 *   - The local `id` (UUID) is sent as `offlineId` with the POST so the server
 *     can de-duplicate if the same order is retried.
 */

import NetInfo from '@react-native-community/netinfo';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';

import {
  countOrdersByStatus,
  getNextPendingOrder,
  getNextRetryDelayMs,
  getOrdersByStatus,
  markOrderFailed,
  markOrderSynced,
  markOrderSyncing,
  resetStuckSyncingOrders,
  scheduleOrderRetry,
} from './database/OfflineSyncDatabase';
import {getAccessToken} from './storage';
import ErrorReportingService from './ErrorReportingService';
import {generateHappyId} from './happyId';
import Observables from '@happy-gastro/global-observables';
import {normalizeOrderType} from './orderVatResolver';
import i18n from '../../i18n';
import {serializeLastOrderResponse} from './orderHistorySlim';
import {buildOrderServiceFeeFields} from './vatSummary';

// ── Constants ───────────────────────────────────────────────────────────────
const API_URL = 'https://api.happysolutions.hu/api';
const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 10 * 1000;
const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;
const OFFLINE_RETRY_DELAY_MS = 15 * 1000;
const ESCALATION_AFTER_MS = 60 * 1000;
const SYNC_TASK_NAME = 'OFFLINE_SYNC_BACKGROUND_TASK';
const LAST_API_PULL_KEY = 'lastApiOrderPullAt';
const PULL_PAGE_SIZE = 100;
const PULL_MIN_INTERVAL_MS = 30 * 1000;

// ── Internal state ──────────────────────────────────────────────────────────
let _isSyncing = false;
let _syncPromise = null;           // Dedup: all callers share the same in-flight sync
let _pendingRerun = false;         // Flag: a new cycle was requested while syncing
let _listeners = [];             // Callbacks notified after every sync cycle
let _netInfoUnsubscribe = null;
let _nextRetryTimeout = null;
let _isPulling = false;
let _lastPullStartedAt = 0;

const emitLocalToApiStatus = (payload = {}) => {
  try {
    Observables.call('offlineSync:status', {
      channel: 'localToApi',
      timestamp: new Date().toISOString(),
      ...payload,
    });
  } catch (_) {
    // Best-effort status reporting only.
  }
};

const emitApiToLocalStatus = (payload = {}) => {
  try {
    Observables.call('offlineSync:status', {
      channel: 'apiToLocal',
      timestamp: new Date().toISOString(),
      ...payload,
    });
  } catch (_) {
    // Best-effort status reporting only.
  }
};

const getRetryDelayMs = (retryCount = 1) => {
  const multiplier = Math.max(0, (retryCount || 1) - 1);
  return Math.min(RETRY_DELAY_MS * (2 ** multiplier), MAX_RETRY_DELAY_MS);
};

const classifySyncError = (error) => {
  const message = String(error?.message || error || 'Unknown sync error');
  const lowered = message.toLowerCase();
  const httpStatus = Number(error?.httpStatus);

  if (error?.name === 'AbortError' || lowered.includes('timeout') || lowered.includes('aborted')) {
    return {
      category: 'timeout',
      userMessage: 'Request timeout while saving order',
      isRetryable: true,
      httpStatus: Number.isFinite(httpStatus) ? httpStatus : null,
    };
  }

  if (error?.syncCategory === 'auth' || httpStatus === 401 || httpStatus === 403) {
    return {
      category: 'auth',
      userMessage: message,
      isRetryable: false,
      httpStatus: Number.isFinite(httpStatus) ? httpStatus : null,
    };
  }

  if (error?.syncCategory === 'api' || Number.isFinite(httpStatus) || lowered.includes('server responded')) {
    const retryableStatus = httpStatus === 408 || httpStatus === 429 || (Number.isFinite(httpStatus) && httpStatus >= 500);
    return {
      category: 'api',
      userMessage: message,
      isRetryable: Boolean(retryableStatus),
      httpStatus: Number.isFinite(httpStatus) ? httpStatus : null,
    };
  }

  if (lowered.includes('network') || lowered.includes('failed to fetch') || lowered.includes('internet')) {
    return {
      category: 'network',
      userMessage: 'Network error while syncing order',
      isRetryable: true,
      httpStatus: null,
    };
  }

  return {
    category: 'unknown',
    userMessage: message,
    isRetryable: true,
    httpStatus: Number.isFinite(httpStatus) ? httpStatus : null,
  };
};

// ── Public helpers ──────────────────────────────────────────────────────────

/**
 * Subscribe to sync-cycle events.  Callback receives { pendingCount, failedCount }.
 * Returns an unsubscribe function.
 */
export const onSyncUpdate = (callback) => {
  _listeners.push(callback);
  return () => {
    _listeners = _listeners.filter(cb => cb !== callback);
  };
};

const _notifyListeners = async () => {
  try {
    const pendingCount = await countOrdersByStatus('Pending');
    const failedCount = await countOrdersByStatus('Failed');
    const syncingCount = await countOrdersByStatus('Syncing');
    _listeners.forEach(cb => {
      try { cb({ pendingCount, failedCount, syncingCount }); } catch (_) { /* swallow */ }
    });
  } catch (e) {
    console.warn('[SyncManager] listener notify error', e.message);
  }
};

const _clearRetryTimer = () => {
  if (_nextRetryTimeout) {
    globalThis.clearTimeout(_nextRetryTimeout);
    _nextRetryTimeout = null;
  }
};

const _scheduleRetryAfterMs = (delayMs = OFFLINE_RETRY_DELAY_MS) => {
  _clearRetryTimer();

  _nextRetryTimeout = globalThis.setTimeout(() => {
    processQueue().catch((err) => {
      console.warn('[SyncManager] Delayed retry failed to start', err?.message);
    });
  }, Math.max(1000, delayMs));
};

const _scheduleNextRetryCycle = async () => {
  _clearRetryTimer();

  try {
    const delayMs = await getNextRetryDelayMs();
    if (delayMs === null) return;

    _nextRetryTimeout = globalThis.setTimeout(() => {
      processQueue().catch((err) => {
        console.warn('[SyncManager] Scheduled retry failed to start', err?.message);
      });
    }, delayMs);
  } catch (err) {
    console.warn('[SyncManager] Failed to schedule next retry cycle', err.message);
  }
};

// ── Core sync loop ──────────────────────────────────────────────────────────

/**
 * Process all Pending orders one-by-one (FIFO).
 * Safe to call multiple times – only one instance runs at a time.
 * If called while a cycle is already running, a follow-up cycle is
 * automatically scheduled so newly inserted orders are picked up.
 */
export const processQueue = () => {
  if (_isSyncing) {
    // A cycle is already running. Flag it so the current cycle triggers
    // another run when it finishes – this ensures orders inserted *during*
    // the current cycle are picked up immediately afterwards.
    _pendingRerun = true;
    console.log('[SyncManager] Already syncing – will re-run after current cycle');
    return _syncPromise || Promise.resolve();
  }

  _syncPromise = _processQueueImpl();
  return _syncPromise;
};

const _processQueueImpl = async () => {
  if (_isSyncing) {
    console.log('[SyncManager] Already syncing – skipping');
    return;
  }

  // Check connectivity first
  const state = await NetInfo.fetch();
  if (!state.isConnected || state.isInternetReachable === false) {
    console.log('[SyncManager] No network – aborting');
    emitLocalToApiStatus({
      state: 'waiting',
      reasonType: 'network',
      reason: 'Device is offline',
    });

    // Keep queue visible in UI even when sync exits early.
    await _notifyListeners();
    _scheduleRetryAfterMs(OFFLINE_RETRY_DELAY_MS);
    return;
  }

  _isSyncing = true;
  emitLocalToApiStatus({ state: 'syncing', reason: null, reasonType: null });

  // Track order IDs already attempted within this sync cycle to avoid
  // re-processing an order that was retried (status set back to Pending
  // with a future next_retry_at).
  const attemptedIds = new Set();

  try {
    // Recover any orders stuck in 'Syncing' from a previous crash/kill
    await resetStuckSyncingOrders();

    // E3: Reconcile against the server first so we never re-POST an order
    // that the backend has already accepted (e.g. the response was lost
    // mid-flight last time). This is best-effort and never blocks the
    // local→server cycle on failure.
    try {
      await _reconcileAlreadySyncedOrders();
    } catch (reconcileErr) {
      console.warn('[SyncManager] Reconciliation skipped:', reconcileErr?.message);
    }

    let order = await getNextPendingOrder();

    while (order) {
      // Guard: never process the same order twice within one sync cycle
      if (attemptedIds.has(order.id)) {
        console.warn(`[SyncManager] Order ${order.id} already attempted this cycle – stopping`);
        break;
      }
      attemptedIds.add(order.id);

      // Re-check connectivity before each POST
      const netState = await NetInfo.fetch();
      if (!netState.isConnected) {
        console.log('[SyncManager] Lost connectivity mid-sync');
        emitLocalToApiStatus({
          state: 'waiting',
          reasonType: 'network',
          reason: 'Connectivity lost during sync',
        });
        break;
      }

      await _syncSingleOrder(order);
      order = await getNextPendingOrder();
    }
  } catch (err) {
    console.error('[SyncManager] Unexpected error', err);
    ErrorReportingService.reportError(err, { source: 'SyncManager.processQueue' });
  } finally {
    _isSyncing = false;
    _syncPromise = null;
    await _notifyListeners();

    // If processQueue() was called while we were busy, run another cycle
    // to pick up orders that were inserted during the previous run.
    if (_pendingRerun) {
      _pendingRerun = false;
      console.log('[SyncManager] Re-running sync for orders added during previous cycle');
      // Use setTimeout(0) to avoid deeply nested call stacks
      globalThis.setTimeout(() => processQueue().catch(() => {}), 0);
    } else {
      await _scheduleNextRetryCycle();
    }
  }
};

/**
 * POST a single order to the API.
 */
const _syncSingleOrder = async (orderRow) => {
  const { id, order_data: orderData, retry_count, first_failure_at, timestamp } = orderRow;

  console.log(`[SyncManager] Syncing order ${id} (attempt ${retry_count + 1})`);

  try {
    await markOrderSyncing(id);
    emitLocalToApiStatus({
      state: 'syncing',
      orderId: id,
      retryCount: retry_count || 0,
    });

    const token = await getAccessToken();
    if (!token) {
      console.warn('[SyncManager] No access token – marking failed');
      const reason = '[auth] No access token available';
      await markOrderFailed(id, reason);
      emitLocalToApiStatus({
        state: 'error',
        orderId: id,
        reason,
        reasonType: 'auth',
        retryCount: (retry_count || 0) + 1,
      });
      return;
    }

    // Build the payload expected by the same backend endpoint used elsewhere
    const payload = await _buildPayload(orderData, id, orderRow);

    const controller = new globalThis.AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), 30000);

    const response = await fetch(`${API_URL}/order/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    globalThis.clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      console.log(`[SyncManager] Order ${id} synced successfully`);

      // Persist the server response for receipt / UI purposes
      await AsyncStorage.setItem('lastOrderResponse', serializeLastOrderResponse(data));
      await markOrderSynced(id);

      // If the backend de-duplicated our device-minted id into a DIFFERENT
      // canonical order (server-side dineIn dedup — the running tab already
      // existed under another id), the optimistic order we inserted locally
      // under `id` is now an orphan. Drop it so it doesn't linger as a ghost
      // "pending" order on an already-occupied table (or keep a paid-and-closed
      // table looking busy). The authoritative order arrives via the reload
      // below / realtime events. When the ids match, this is a no-op.
      const canonicalOrderId = data?._id || data?.order?._id;
      if (canonicalOrderId && String(canonicalOrderId) !== String(id)) {
        try {
          Observables.call('orders:dropOptimistic', id);
        } catch (_) { /* ignore if not registered yet */ }
      }

      // Immediately notify listeners so the badge updates right away
      await _notifyListeners();

      // Trigger immediate order list refresh so the new order appears right away
      try {
        Observables.call('reloadOrders');
      } catch (_) { /* ignore if not registered yet */ }

      emitLocalToApiStatus({
        state: 'success',
        orderId: id,
        reason: null,
        reasonType: null,
      });
    } else {
      const errorText = await response.text().catch(() => 'Unknown');
      const apiError = new Error(`Server responded ${response.status}: ${errorText}`);
      apiError.syncCategory = response.status === 401 || response.status === 403 ? 'auth' : 'api';
      apiError.httpStatus = response.status;
      throw apiError;
    }
  } catch (err) {
    console.error(`[SyncManager] Failed to sync order ${id}:`, err.message);

    const newRetry = (retry_count || 0) + 1;
    const firstFailureTime = first_failure_at ? new Date(first_failure_at).getTime() : Date.now();
    const orderCreatedTime = timestamp ? new Date(timestamp).getTime() : Date.now();
    const ageReference = Number.isFinite(firstFailureTime) ? firstFailureTime : orderCreatedTime;
    const shouldEscalate = (Date.now() - ageReference) >= ESCALATION_AFTER_MS || newRetry >= MAX_RETRIES;
    const details = classifySyncError(err);
    const normalizedReason = `[${details.category}] ${details.userMessage}`;

    // Dead-letter: a "retryable" error that has already exhausted MAX_RETRIES
    // must NOT be rescheduled forever (every ~5 min indefinitely). Once the
    // attempt budget is spent we move the order to the terminal 'Failed' state
    // so it stops auto-retrying and waits for an explicit manual retry/dismiss
    // from the Sync Center. Non-retryable errors already dead-letter below.
    const exhaustedRetries = details.isRetryable && newRetry >= MAX_RETRIES;

    if (details.isRetryable && !exhaustedRetries) {
      const retryDelay = getRetryDelayMs(newRetry);
      await scheduleOrderRetry(id, normalizedReason, retryDelay);

      emitLocalToApiStatus({
        state: 'retrying',
        orderId: id,
        reason: normalizedReason,
        reasonType: details.category,
        retryCount: newRetry,
        nextRetryInMs: retryDelay,
        httpStatus: details.httpStatus,
      });

      if (shouldEscalate) {
        Observables.call('offlineSync:attentionRequired', {
          orderId: id,
          errorMessage: normalizedReason,
          retryCount: newRetry,
        });
        _fireFailureNotification(id);
      }
    } else {
      await markOrderFailed(id, normalizedReason);
      Observables.call('offlineSync:attentionRequired', {
        orderId: id,
        errorMessage: normalizedReason,
        retryCount: newRetry,
      });
      _fireFailureNotification(id);

      emitLocalToApiStatus({
        state: 'error',
        orderId: id,
        reason: normalizedReason,
        reasonType: details.category,
        retryCount: newRetry,
        httpStatus: details.httpStatus,
      });
    }

    // Notify listeners after status change so badge reflects current state
    await _notifyListeners();
  }
};

/**
 * Construct the API payload from local order data.
 * This mirrors the payload shape used by BackgroundTasks/triggerStoreOrder.
 *
 * IMPORTANT: generatedId and orderCode are computed ONCE when the order is
 * first queued (stored in the DB row) so that retries do not create
 * duplicate orders on the server.
 *
 * Exported additively so the wire contract can be unit tested without running a
 * live sync cycle; the sync loop keeps calling it exactly as before.
 *
 * @param {object} orderData The queued order record (as inserted by send_storeOrder).
 * @param {string} offlineId Local queue id, used as the fallback generatedId.
 * @param {object} [orderRow] The SQLite row the order was read from.
 * @returns {Promise<object>} The POST body for /order.
 */
export const _buildPayload = async (orderData, offlineId, orderRow) => {
  const {
    cartTotal, cartItems, paymentType, paidAmount,
    discountsForWholeCart, vatInvoiceData, needInvoice,
    currentTable, serviceFeeAmount, isSavedForLater,
    alreadySentCartItems, orderId, paymentTypes, tip,
    selectedVipCard, userData,
    selectedItemsToPay, order, proform, deliveryFee,
    // Stable IDs stored at insertion time – never regenerated on retry
    _stableGeneratedId, _stableOrderCode,
    // Set by send_storeOrder when this payment settles the whole table tab
    _settlesTableOrder,
  } = orderData;

  const restaurantData = await AsyncStorage.getItem('restaurant');
  const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

  if (!restaurantData || !restaurantPlaceData) {
    throw new Error('Missing restaurant metadata required to sync order');
  }

  const restaurant = JSON.parse(restaurantData);
  const restaurantPlace = JSON.parse(restaurantPlaceData);

  const convertPaymentType = (type) => {
    if (!type) return 'temporary';
    if (type === 'Készpénz' || type === 'cash') return 'cash';
    if (type === 'Bankkártya' || type === 'creditCard') return 'creditCard';
    if (type === 'Utalvány' || type === 'voucher') return 'voucher';
    if (type === 'VIP' || type === 'vipBalance') return 'vipBalance';
    if (type === 'Személyzeti' || type === 'employeeBalance') return 'employeeBalance';
    return type;
  };

  const normalizedPaymentTypes = paymentTypes && paymentTypes.length > 0
    ? paymentTypes.map((payment) => ({
        ...payment,
        paymentType: convertPaymentType(payment.paymentType),
      }))
    : [
        {
          paymentType: convertPaymentType(paymentType),
          amount: paidAmount || cartTotal || 0,
          change: 1,
          currency: 'HUF',
        },
      ];

  const itemsArrayforOrder = [
    ...((cartItems ?? []).map(item => ({
      item: item.itemId || item.item?._id || item.item,
      sizeIndex: item?.sizeIndex || 0,
      size: item?.sizes?.[item?.sizeIndex || 0]?._id || item?.size || item?.item?.sizes?.[item?.sizeIndex || 0]?._id,
      quantity: (item.amount || item.quantity || 1),
      extras: item.extras || [],
      serveType: item.serveType || '',
      comments: item.comments || [],
      discounts: item.discounts || {},
      round: item.round || 0,
      amount: (item.amount || item.quantity || 1),
      price: item.price,
      remotePrice: item.remotePrice || item.price,
    }))),
    ...((alreadySentCartItems ?? []).map(item => ({
      item: item.item?._id || item.itemId || item.item,
      sizeIndex: item.sizeIndex ?? item.size ?? 0,
      size: item.size || item?.sizes?.[item?.sizeIndex || 0]?._id,
      quantity: (item.amount || item.quantity || 0),
      extras: item.extras || [],
      serveType: item.serveType || '',
      comments: item.comments || [],
      discounts: item.discounts || {},
      round: item.round || 0,
      amount: (item.amount || item.quantity || 0),
      price: item.price,
      remotePrice: item.remotePrice || item.price,
    }))),
  ];

  const invoiceData = vatInvoiceData ? {
    company: {
      addressData: {
        fullAddress: vatInvoiceData?.fullAddress || '',
        city: vatInvoiceData?.city || '',
        postCode: vatInvoiceData?.postCode || '',
        street: vatInvoiceData?.address || '',
        streetType: vatInvoiceData?.streetType || '',
        houseNumber: vatInvoiceData?.houseNumber || '',
      },
      nev: vatInvoiceData?.name || '',
      adoszam: vatInvoiceData?.taxRefNum || '',
      cim: vatInvoiceData?.fullAddress || '',
      cegj_sz: vatInvoiceData?.cegj_sz || '',
      arbev: vatInvoiceData?.arbev || '',
    },
    firstName: vatInvoiceData?.firstName || '',
    lastName: vatInvoiceData?.lastName || '',
    email: vatInvoiceData?.email || '',
    phone: vatInvoiceData?.phone || '',
    comment: vatInvoiceData?.comment || '',
  } : null;

  const selectedItems = selectedItemsToPay ? { selectedItems: selectedItemsToPay } : {};
  const computedOrderType = (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable)
    ? 'dineIn'
    : normalizeOrderType(userData?.orderType, 'takeAway');

  // Use the stable IDs that were generated once when the order was first
  // inserted into the queue.  This prevents duplicate orders on the server
  // when retries hit the API with a new generatedId each time.
  const stableGenId = _stableGeneratedId
    || (computedOrderType === 'takeAway' || !currentTable?._id ? undefined : orderId)
    || offlineId;
  const stableOrderCode = _stableOrderCode
    || (computedOrderType === 'takeAway' || !currentTable?._id ? undefined : order?.orderCode);

  // The backend re-derives the fee from the `serviceFee` percentage, so it is
  // only sent when a fee was actually charged. Sending the place's configured
  // rate while `showServiceFee` is off made the server expect a fee nobody paid:
  // paid < payable, so a fully paid table tab was never closed.
  const serviceFeeFields = currentTable?._id
    ? buildOrderServiceFeeFields(serviceFeeAmount, restaurantPlace?.serviceFee)
    : { serviceFee: 0, serviceFeePrice: 0 };

  const bodyData = {
    offlineId,
    VIPCard: userData ? null : (selectedVipCard ? selectedVipCard?._id : null),
    generatedId: stableGenId || offlineId,
    orderCode: stableOrderCode || generateHappyId(),
    restaurant: restaurant._id,
    addressInfo: userData?.addressInfo || {},
    userInfo: userData?.userInfo || {},
    placeId: restaurantPlace._id,
    preOrder: userData?.preOrder || null,
    orderType: computedOrderType,
    serviceFee: serviceFeeFields.serviceFee,
    deliveryFee: deliveryFee || 0,
    editOrderSum: alreadySentCartItems && alreadySentCartItems.length > 0
      ? alreadySentCartItems.reduce((sum, item) => sum + (item.remotePrice || item.price) * (item.amount || item.quantity || 1), 0)
      : undefined,
    serviceFeePrice: serviceFeeFields.serviceFeePrice,
    serviceFeeName: '',
    orderFrom: restaurantPlace._id,
    selectedPortal: userData?.selectedPortal || restaurantPlace._id,
    proforma: proform || false,
    tableId: currentTable?._id || '',
    tableName: currentTable?.name || '',
    tipAmount: tip || 0,
    comment: '',
    ...selectedItems,
    discounts: discountsForWholeCart,
    paymentType: normalizedPaymentTypes[0]?.paymentType || convertPaymentType(paymentType),
    paymentTypes: isSavedForLater ? [] : normalizedPaymentTypes,
    items: [...itemsArrayforOrder],
    originalItems: [...itemsArrayforOrder],
    cartItems: [],
    needInvoice: needInvoice || false,
  };

  if (isSavedForLater) {
    bodyData.isPayed = false;
  } else if (_settlesTableOrder === true) {
    // Full payment of the table's open tab: the backend closes it even if its
    // own re-pricing differs from the charged total (see backend
    // isExplicitDineInSettlement), so the table is freed on every terminal.
    bodyData.closeOrder = true;
  }

  // ── Sales module link (additive) ──────────────────────────────────────────
  // A field-sales order is sold TO someone: it points at a unified customer
  // (`customer` + its `customerModel` refPath), optionally at the `lead` it was
  // sold from, and at the `salesUser` credited with it. The link rides on
  // `userData.salesLink` (see helpers/salesOrder.js) because that is the only
  // slot that reaches this builder untouched.
  //
  // It is emitted as SEPARATE top-level fields on purpose: `VIPCard` above is
  // nulled for every order that carries `userData`, so a customer smuggled
  // through the VIP card would be silently dropped on every sales order. Each
  // field is written only when it actually has a value, so a POS/dine-in order
  // (which never sets `salesLink`) produces a byte-identical payload.
  const salesLink = userData?.salesLink || null;

  if (salesLink) {
    if (salesLink.customer) {
      bodyData.customer = salesLink.customer;
      bodyData.customerModel = salesLink.customerModel || 'Customer';
    }
    if (salesLink.lead) {
      bodyData.lead = salesLink.lead;
    }
    if (salesLink.salesUser) {
      bodyData.salesUser = salesLink.salesUser;
    }
    if (salesLink.comment) {
      bodyData.comment = String(salesLink.comment);
    }
  }

  return invoiceData ? { ...bodyData, invoiceData } : bodyData;
};

/**
 * Fire a system notification when an order exceeds MAX_RETRIES.
 */
const _fireFailureNotification = async (orderId) => {
  try {
    if (Platform.OS !== 'web') {
      const Notifications = require('expo-notifications');
      const { ensureNotificationChannels } = require('./notificationChannels');
      await ensureNotificationChannels();
      await Notifications.scheduleNotificationAsync({
        content: {
          title: i18n.t('sync_order_failed_title'),
          body: i18n.t('sync_order_failed_body', { orderId, attempts: MAX_RETRIES }),
          data: { orderId },
          // Sync failures are operational alerts → the localized 'system' channel
          // (Android) / system thread (iOS), not the generic default channel.
          ...(Platform.OS === 'android' ? { channelId: 'system' } : { threadIdentifier: 'system' }),
        },
        trigger: null, // fire immediately
      });
    }
  } catch (e) {
    console.warn('[SyncManager] Notification failed', e.message);
  }
};

// ── Server → Local catch-up (E2/E3) ─────────────────────────────────────────

/**
 * Build the cursor URL used for both reconciliation and full pulls.
 * Returns null when the device is missing the metadata required to query.
 */
const _buildPullUrl = async ({sinceIso, limit = PULL_PAGE_SIZE} = {}) => {
  const restaurantData = await AsyncStorage.getItem('restaurant');
  const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');
  if (!restaurantData || !restaurantPlaceData) return null;

  let restaurant;
  let restaurantPlace;
  try {
    restaurant = JSON.parse(restaurantData);
    restaurantPlace = JSON.parse(restaurantPlaceData);
  } catch (_) {
    return null;
  }

  const params = new globalThis.URLSearchParams();
  if (restaurant?._id) params.set('restaurant', restaurant._id);
  if (restaurantPlace?._id) params.set('placeId', restaurantPlace._id);
  if (sinceIso) params.set('updatedSince', sinceIso);
  params.set('limit', String(limit));

  return `${API_URL}/order?${params.toString()}`;
};

const _fetchWithAuthAndTimeout = async (url, {timeoutMs = 20000} = {}) => {
  const token = await getAccessToken();
  if (!token) {
    const err = new Error('No access token');
    err.syncCategory = 'auth';
    err.httpStatus = 401;
    throw err;
  }

  const controller = new globalThis.AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });
    return res;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
};

const _extractOrdersFromResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

/**
 * Pull orders updated on the server since the last successful pull.
 * De-duplicates by `(generatedId || offlineId || orderCode || _id)` against
 * orders already present locally. Updates the `lastApiOrderPullAt` cursor
 * on success. Safe to call repeatedly – will throttle to once per
 * PULL_MIN_INTERVAL_MS unless `force` is true.
 */
export const pullOrdersSince = async ({force = false} = {}) => {
  if (_isPulling) return {ok: false, reason: 'in_progress'};
  if (!force && Date.now() - _lastPullStartedAt < PULL_MIN_INTERVAL_MS) {
    return {ok: false, reason: 'throttled'};
  }

  const netState = await NetInfo.fetch();
  if (!netState.isConnected || netState.isInternetReachable === false) {
    return {ok: false, reason: 'offline'};
  }

  _isPulling = true;
  _lastPullStartedAt = Date.now();
  emitApiToLocalStatus({state: 'syncing', reason: null, reasonType: null});

  try {
    const sinceIso = (await AsyncStorage.getItem(LAST_API_PULL_KEY)) || null;
    const url = await _buildPullUrl({sinceIso});
    if (!url) {
      emitApiToLocalStatus({state: 'idle', reason: 'no_metadata', reasonType: 'config'});
      return {ok: false, reason: 'missing_metadata'};
    }

    const response = await _fetchWithAuthAndTimeout(url);
    if (!response.ok) {
      emitApiToLocalStatus({
        state: 'error',
        reason: `Server responded ${response.status}`,
        reasonType: response.status === 401 || response.status === 403 ? 'auth' : 'api',
      });
      return {ok: false, reason: 'http_error', httpStatus: response.status};
    }

    let data;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    const remoteOrders = _extractOrdersFromResponse(data);
    const localPending = await getOrdersByStatus(['Pending', 'Syncing', 'Failed']);
    const localIds = new Set();
    localPending.forEach((row) => {
      const od = row?.order_data || {};
      [
        row?.id,
        od?._stableGeneratedId,
        od?._stableOrderCode,
        od?.order?.orderCode,
      ].forEach((v) => { if (v) localIds.add(String(v)); });
    });

    let acceptedCount = 0;
    const dedupedRemote = [];
    const remoteSeen = new Set();

    for (const remote of remoteOrders) {
      const dedupKey = String(
        remote?.generatedId
        || remote?.offlineId
        || remote?.orderCode
        || remote?._id
        || ''
      );
      if (!dedupKey || remoteSeen.has(dedupKey)) continue;
      remoteSeen.add(dedupKey);

      // If this server order corresponds to a locally pending one, mark
      // the local row as synced (E3 reconciliation).
      const matchKeys = [
        remote?.offlineId,
        remote?.generatedId,
        remote?.orderCode,
      ].filter(Boolean).map(String);

      let matchedLocalId = null;
      for (const row of localPending) {
        const od = row?.order_data || {};
        const candidates = [
          row?.id,
          od?._stableGeneratedId,
          od?._stableOrderCode,
          od?.order?.orderCode,
        ].filter(Boolean).map(String);
        if (matchKeys.some((k) => candidates.includes(k))) {
          matchedLocalId = row.id;
          break;
        }
      }

      if (matchedLocalId) {
        try {
          await markOrderSynced(matchedLocalId);
          acceptedCount += 1;
        } catch (_) { /* swallow */ }
        continue;
      }

      dedupedRemote.push(remote);
    }

    // Persist a "lastOrderResponse" snapshot for the most recent remote order
    // so the existing UI flows can pick it up (mirrors success path of
    // local→server sync).
    if (dedupedRemote.length > 0) {
      try {
        await AsyncStorage.setItem('lastOrderResponse', serializeLastOrderResponse(dedupedRemote[dedupedRemote.length - 1]));
      } catch (_) { /* ignore */ }
    }

    // Push remote orders into the in-memory order list via the existing
    // observable pipeline so screens can refresh without us needing to
    // touch the orders cache schema.
    if (dedupedRemote.length > 0 || acceptedCount > 0) {
      try { Observables.call('reloadOrders', {fromServerPull: true, count: dedupedRemote.length}); } catch (_) { /* ignore */ }
    }

    await AsyncStorage.setItem(LAST_API_PULL_KEY, new Date().toISOString());

    emitApiToLocalStatus({
      state: 'success',
      reason: null,
      reasonType: null,
      pulled: dedupedRemote.length,
      reconciled: acceptedCount,
    });

    return {ok: true, pulled: dedupedRemote.length, reconciled: acceptedCount};
  } catch (err) {
    console.warn('[SyncManager] pullOrdersSince failed:', err?.message);
    emitApiToLocalStatus({
      state: 'error',
      reason: String(err?.message || err),
      reasonType: err?.syncCategory || 'unknown',
    });
    return {ok: false, reason: 'error', error: err};
  } finally {
    _isPulling = false;
  }
};

/**
 * Lightweight pre-check at the start of every sync cycle: pull what the
 * server already has so locally-pending orders that the backend already
 * accepted are not re-POSTed (which would surface as duplicates on the
 * receipt printer or on dashboards).
 */
const _reconcileAlreadySyncedOrders = async () => {
  // Don't slam the server every cycle – the throttle inside pullOrdersSince
  // already handles that, but we also explicitly want to skip if there is
  // no pending work to reconcile.
  const pendingCount = await countOrdersByStatus('Pending');
  if (pendingCount === 0) return;

  await pullOrdersSince({force: false});
};

// ── Network listener ────────────────────────────────────────────────────────

/**
 * Start listening for connectivity changes.
 * Triggers processQueue when the device comes back online.
 */
export const startNetworkListener = () => {
  if (_netInfoUnsubscribe) return; // already listening

  _netInfoUnsubscribe = NetInfo.addEventListener((state) => {
    if (state.isConnected && state.isInternetReachable !== false) {
      console.log('[SyncManager] Device online – kicking sync');
      processQueue().catch(() => {});
      // Also catch up on any orders the server received while we were
      // offline (E2). Throttled internally so this is safe to call freely.
      pullOrdersSince({force: true}).catch(() => {});
    }
  });

  // Immediate initial check
  NetInfo.fetch().then((state) => {
    if (state.isConnected && state.isInternetReachable !== false) {
      processQueue().catch(() => {});
      pullOrdersSince({force: false}).catch(() => {});
    }
  });
};

/**
 * Stop the network listener (cleanup).
 */
export const stopNetworkListener = () => {
  if (_netInfoUnsubscribe) {
    _netInfoUnsubscribe();
    _netInfoUnsubscribe = null;
  }

  _clearRetryTimer();
};

// ── Background task (expo-task-manager) ─────────────────────────────────────

if (Platform.OS !== 'web') {
  TaskManager.defineTask(SYNC_TASK_NAME, async () => {
    try {
      const netState = await NetInfo.fetch();
      if (!netState.isConnected) {
        return BackgroundFetch.BackgroundFetchResult.NoData;
      }

      // Background tick: catch up on server-side changes first, then
      // flush any locally pending orders.
      await pullOrdersSince({force: true}).catch(() => {});
      await processQueue();
      const remaining = await countOrdersByStatus('Pending');
      return remaining > 0
        ? BackgroundFetch.BackgroundFetchResult.NewData
        : BackgroundFetch.BackgroundFetchResult.NoData;
    } catch (err) {
      console.error('[SyncManager] Background task error', err);
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  });
}

/**
 * Register the background fetch task so sync runs even when the app is
 * minimized. Safe to call multiple times.
 */
export const registerBackgroundSync = async () => {
  if (Platform.OS === 'web') return;

  try {
    const isRegistered = await TaskManager.isTaskRegisteredAsync(SYNC_TASK_NAME);
    if (!isRegistered) {
      await BackgroundFetch.registerTaskAsync(SYNC_TASK_NAME, {
        minimumInterval: 15 * 60, // 15 minutes
        stopOnTerminate: false,
        startOnBoot: true,
      });
      console.log('[SyncManager] Background sync task registered');
    }
  } catch (err) {
    console.warn('[SyncManager] Failed to register background task', err.message);
  }
};

export default {
  processQueue,
  onSyncUpdate,
  startNetworkListener,
  stopNetworkListener,
  registerBackgroundSync,
  pullOrdersSince,
};




